import { Card, Col, Row, Skeleton, Statistic, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { centsToDollarsDisplay } from '../../displayFormatters'
import { useGetTenantReport } from './queries'
import { roundToPrecision } from '../../../helpers/math'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'

const TenantReport = () => {
  const weekStartDate = dayjs().startOf('week').add(-7, 'day')
  const weekEndDate = dayjs().endOf('week').add(-7, 'day')
  const startDateFormatted = weekStartDate.format('YYYY-MM-DD')
  const endDateFormatted = weekEndDate.format('YYYY-MM-DD')
  const tenantReportQuery = useGetTenantReport(startDateFormatted, endDateFormatted)
  const tenantReport = tenantReportQuery.data

  if (tenantReportQuery.isLoading) {
    return <>
      <Skeleton active></Skeleton>
    </>
  }

  return <>
  <Row>
    <Col span={5}>
      Week of - {startDateFormatted} to {endDateFormatted}
    </Col>
  </Row>
    <Card>
      Tenant Data
      <Row gutter={16}>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Total Tenants" value={tenantReport?.totalTenants} />
          </Card>
        </Col>
      </Row>
    </Card>
    <Card>
      Sold Jobs Data
      <Row>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Total Sold Jobs Tenants" value={tenantReport?.totalSoldJobsTenants} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Sold Jobs" value={tenantReport?.soldJobs} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Total Final Price of Sold Jobs" value={centsToDollarsDisplay(tenantReport?.totalFinalPrice)?.toString()} precision={2} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="AVG Sold Jobs Amount($) per Tenant with Sold Jobs" value={centsToDollarsDisplay( ( tenantReport?.totalFinalPrice ?? 0 ) / (tenantReport?.totalSoldJobsTenants ?? 0) )?.toString()} precision={2} />
          </Card>
        </Col>
      </Row>
    </Card>
    <Card>
      Proposal Data
      <Row gutter={16}>
      <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Proposals Created" value={tenantReport?.totalProposalsCreated} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Proposals Sent" value={tenantReport?.totalProposalsSent} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Proposals Follow Ups Sent" value={tenantReport?.totalProposalFollowUps} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Proposal Portal Views" value={tenantReport?.totalProposalPortalViews} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic title="Proposals Approved" value={tenantReport?.totalProposalsApproved} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic 
              title={<>Close Rate YTD
                <Tooltip title={'Total Sent and Approved Follow Up Proposals: ' + tenantReport?.currentYearApprovedProposals + 'Total Sent Follow Up Proposals: ' + tenantReport?.currentYearSentProposals }>
                  <FontAwesomeIcon icon={faInfoCircle} /> 
                </Tooltip></>
              }    
              value={roundToPrecision(( (tenantReport?.currentYearApprovedProposals ?? 0) / (tenantReport?.currentYearSentProposals ?? 0)) * 100, 2) + '%'} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic 
              title={<>Close Rate Proposals without Follow Ups YTD
                <Tooltip title={'Sent and Approved Not Follow Up Proposals: ' + tenantReport?.currentYearSentNonApprovedFollowUpProposals + ' Sent Not Follow Up Proposals: ' + tenantReport?.currentYearSentNonFollowUpProposals }>
                  <FontAwesomeIcon icon={faInfoCircle} /> 
                </Tooltip></>
              }   
              value={roundToPrecision(( (tenantReport?.currentYearSentNonApprovedFollowUpProposals ?? 0) / (tenantReport?.currentYearSentNonFollowUpProposals ?? 0)) * 100, 2) + '%'} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic 
              title={<>Close Rate Proposals with Follow Ups YTD
                <Tooltip title={'Sent and Approved Follow Up Proposals: ' + tenantReport?.currentYearSentApprovedFollowUpProposals + ' Sent Follow Up Proposals: ' + tenantReport?.currentYearSentFollowUpProposals }>
                  <FontAwesomeIcon icon={faInfoCircle} /> 
                </Tooltip></>
              }  
              value={roundToPrecision(( (tenantReport?.currentYearSentApprovedFollowUpProposals ?? 0) / (tenantReport?.currentYearSentFollowUpProposals ?? 0)) * 100, 2) + '%'} />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            Follow up Approved Steps YTD
            <Table
              columns={[
                {
                  title: 'Approved Step',
                  dataIndex: 'approvedStep',
                  key: 'approvedStep',
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                },
              ]}
              dataSource={tenantReport?.followUpApprovedOnStepData ?? []}
              pagination={{ pageSize: 5,  }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic 
              title={<>Days to Close (Sent to Approved) YTD 
                <Tooltip title={'Total Days: ' + tenantReport?.daysToCloseData.totalDaysToClose + ' Total Proposals: ' + tenantReport?.daysToCloseData.proposalTotal }>
                  <FontAwesomeIcon icon={faInfoCircle} /> 
                </Tooltip></>
              } 
              value={roundToPrecision(( (tenantReport?.daysToCloseData.totalDaysToClose ?? 0) / (tenantReport?.daysToCloseData.proposalTotal ?? 0)), 2) + ' days'} 
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            Days to Close YTD
            <Table
              columns={[
                {
                  title: 'Close Day',
                  dataIndex: 'daysToClose',
                  key: 'daysToClose',
                },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                },
              ]}
              dataSource={tenantReport?.dayItClosedData ?? []}
              pagination={{ pageSize: 5,  }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card bordered={true}>
            <Statistic 
              title={<>Proposal Turnaround in Days (Job Creation to Proposal Sent) YTD 
                <Tooltip title={'Total Turnaround in Days: ' + tenantReport?.proposalTurnaroundData.totalProposalTurnaroundDays + ' Total Proposals: ' + tenantReport?.proposalTurnaroundData.proposalTotal }>
                  <FontAwesomeIcon icon={faInfoCircle} /> 
                </Tooltip></>
              } 
              value={roundToPrecision(( (tenantReport?.proposalTurnaroundData.totalProposalTurnaroundDays ?? 0) / (tenantReport?.proposalTurnaroundData.proposalTotal ?? 0)), 2) + ' days'} 
            />
          </Card>
        </Col>
      </Row>
    </Card>
  </>
}

export default TenantReport